import React from 'react'
import { Image, Paragraph, WhiteTheme } from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { NoticeLoading } from '@fs/zion-icon'
import goldArrowDividerStickerSrc from '../../assets/stickers/gold-arrow-divider.svg'
import Caption from '../../components/PageAssembler/InnerComponents/ImagePicker/Caption/Caption'
import { useMemoryBook } from '../../providers/MemoryBookProvider'
import { getHasDarkBackground } from '../../helpers/getHasDarkBackground'
import useIsSafari from '../../hooks/useIsSafari/useIsSafari'

const backPageCSS = (isSafari) => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 100%;
  width: 100%;

  @media print {
    min-height: ${isSafari ? '100vh' : '80vh'};
  }
`

export default function BackPage({ pageNumber, elements, isPrintView = false }) {
  const [t, i18n] = useTranslation()
  const { displayName, userLoading } = useUser()
  const { memoryBook } = useMemoryBook()
  const isSafari = useIsSafari()
  const hasDarkBackground = getHasDarkBackground(pageNumber, memoryBook)

  const options = {
    year: 'numeric',
    month: 'long', // Full month name (e.g., November)
    day: 'numeric',
  }

  const formatDate = () => {
    // Use the current language from i18n, or fall back to 'en-US'
    const locale = i18n.language || 'en-US'
    return new Date().toLocaleDateString(locale, options)
  }

  const formattedDate = formatDate()

  // Extract title with a default value of "By: Name"
  const title = elements?.props?.title || t('BackPage.defaultTitle', 'By: {name}', { name: displayName })

  return (
    <div css={backPageCSS(isSafari)} data-testid="back-cover-page-template">
      {userLoading ? (
        <NoticeLoading size="md" />
      ) : (
        <>
          <div>
            <Caption
              pageNumber={pageNumber}
              positionIndex={0}
              isHeading
              title={title}
              memoryId={elements?.props?.memoryId}
              isPrintView={isPrintView}
            />
          </div>
          <Image
            src={goldArrowDividerStickerSrc}
            alt={t('Sticker.goldArrowDivider.label', 'Gold Arrow Divider')}
            style={{ maxHeight: '10px', maxWidth: '80%' }}
          />
          {hasDarkBackground ? (
            <WhiteTheme>
              <Paragraph size="sm" centered>
                {formattedDate}
              </Paragraph>
            </WhiteTheme>
          ) : (
            <Paragraph size="sm" centered>
              {formattedDate}
            </Paragraph>
          )}
        </>
      )}
    </div>
  )
}
